/**
 * Uploads (contracts/ui-api.md "New run" and "Rejected"; mission 2
 * upload-api.md; P.3, U.1, U.3, U.4, SEC.8; C-006).
 *
 * `grantUpload` is called only from `lib/files.uploadFile`, which runs the
 * grant, the PUT and the completion in one function, so the presigned URL never
 * reaches a TanStack cache (post-plan S9).
 */
import { api, idSegment } from '../api'
import type { RunPath } from './runs'

export type UploadState =
  | 'granted'
  | 'validating'
  | 'validated'
  | 'rejected'
  | 'refused'
  | 'expired'
  | 'consumed'
  | (string & {})

export interface GrantInput {
  path: RunPath
  filename: string
  /** Resolved by the screen (Path B `.dat` is application/octet-stream; post-tasks E5). */
  content_type: string
  size_bytes: number
}

/** 201 from POST /v1/uploads. The PUT sends exactly `headers` (P.3). */
export interface UploadGrant {
  upload_id: string
  url: string
  method: string
  headers: Record<string, string>
  expires_at: string
  max_bytes: number
}

/** One inline validation error; `got` is the customer's value, rendered as text only (S12). */
export interface ValidationError {
  row: number
  column: string
  expected: string
  got: string
  hint: string
}

/** GET /v1/uploads/{id} and the completion body. */
export interface Upload {
  upload_id: string
  state: UploadState
  filename?: string
  size_bytes?: number
  record_count?: number
  /** A rejected completion names the run that records it. */
  run_id?: string
  error_count?: number
  errors_by_column?: Record<string, number>
  errors?: ValidationError[]
  report_truncated?: boolean
  report_url?: string
}

/** GET /v1/uploads/{id}/estimate (B.3, B.5). */
export interface Estimate {
  upload_id: string
  record_count: number
  required: number
  balance_before: number
  balance_after: number
  blocked: boolean
  /** Rendered verbatim. */
  message: string
  soft_cap_active: boolean
  /** Present only when blocked. */
  shortfall?: number
}

/** POST /v1/uploads. Path A declares layout v1 (U.1); Path B declares no layout. */
export async function grantUpload(input: GrantInput): Promise<UploadGrant> {
  const body =
    input.path === 'generate'
      ? {
          path: 'generate',
          filename: input.filename,
          content_type: input.content_type,
          size_bytes: input.size_bytes,
          layout_version: 'v1',
        }
      : { path: 'analyze', filename: input.filename, content_type: input.content_type, size_bytes: input.size_bytes }
  return api<UploadGrant>('/uploads', { method: 'POST', body: JSON.stringify(body) })
}

export async function completeUpload(id: string): Promise<Upload> {
  return api<Upload>(`/uploads/${idSegment(id, 'upload id')}/complete`, { method: 'POST' })
}

export async function getUpload(id: string): Promise<Upload> {
  return api<Upload>(`/uploads/${idSegment(id, 'upload id')}`)
}

export async function getEstimate(id: string): Promise<Estimate> {
  return api<Estimate>(`/uploads/${idSegment(id, 'upload id')}/estimate`)
}
